import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCcw, Users } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/lib/api';
import { compactText, getStatusTone } from '@/lib/legalOs';
import { loadTodaySummary } from '@/lib/workflowData';
import { AppEmptyState, AppPage, AppPageHeader } from '@/components/layout/AppPage';
import WorkflowCompatibilityNotice from '@/components/layout/WorkflowCompatibilityNotice';

export default function TeamOpsPage() {
  const { getAuthHeaders, user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadNotice, setLoadNotice] = useState('');

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const { data, notice } = await loadTodaySummary({
        apiBase: API,
        headers: getAuthHeaders(),
        user,
      });
      setSummary(data);
      setLoadNotice(notice);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, user]);

  useEffect(() => {
    document.title = 'Team Ops — Advoq';
    loadSummary();
  }, [loadSummary]);

  return (
    <main className="app-main" data-testid="team-ops-page">
      <AppPage>
        <AppPageHeader
          eyebrow="Team Ops"
          title="Chamber tasking and review queue"
          description="Junior instructions, ownership, blocked work, and shared matter state in one queue."
          actions={(
            <button type="button" className="app-chat-button" onClick={loadSummary} disabled={loading}>
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
              Refresh
            </button>
          )}
        />
        <WorkflowCompatibilityNotice message={loadNotice} />

        {!loading && !(summary?.team_tasks || []).length ? (
          <AppEmptyState
            icon={Users}
            title="No team tasks are exposed yet"
            description="As matter ops begins generating and capturing delegated work, the team queue will appear here."
          />
        ) : null}

        <div style={{ display: 'grid', gap: '14px' }}>
          {(summary?.team_tasks || []).map((item, index) => {
            const tone = getStatusTone(item.status);
            return (
              <div key={`${item.id || item.title}_${index}`} className="advoq-card" style={{ padding: '16px', borderRadius: '22px', border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', display: 'grid', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                  <div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '15px', fontWeight: 700 }}>{item.title}</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6 }}>{compactText(item.description, 220)}</div>
                  </div>
                  <span className="app-page-chip" style={{ background: tone.background, color: tone.color, border: `1px solid ${tone.border}` }}>{item.status || 'watch'}</span>
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                  {item.matter_title ? `${item.matter_title} · ` : ''}{item.owner || 'Owner unassigned'}
                </div>
              </div>
            );
          })}
        </div>
      </AppPage>
    </main>
  );
}
